import React, { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, Camera, Printer } from "lucide-react";

const GROUP_META = {
  "Public / Auth": { accent: "#426db0", blurb: "Sign-in, self-registration and password recovery" },
  "Brand / Customer": { accent: "#f59e0b", blurb: "What your brand clients see when they log in" },
  "BoxHero Staff / Ops": { accent: "#10b981", blurb: "The internal warehouse & operations console" },
};

const PRINT_CSS = `
@media print {
  @page { size: A4 landscape; margin: 8mm; }
  html, body { background: #ffffff !important; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  [data-testid="gallery-page"], .gallery-print-only { background: #ffffff !important; }
  .gallery-screen-only { display: none !important; }
  .gallery-print-only { display: block !important; }
  .print-slide { page-break-after: always; break-after: page; background: #ffffff; }
  .print-slide:last-child { page-break-after: auto; }
}
`;

export default function Gallery() {
  const [shots, setShots] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetch("/gallery/manifest.json")
      .then((r) => r.json())
      .then(setShots)
      .catch(() => setShots([]));
  }, []);

  const groups = useMemo(() => {
    const order = [];
    const map = {};
    shots.forEach((s) => {
      if (!map[s.group]) { map[s.group] = []; order.push(s.group); }
      map[s.group].push(s);
    });
    return order.map((g) => ({ name: g, items: map[g] }));
  }, [shots]);

  const flat = shots;
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
    const onKey = (e) => {
      if (active === null) return;
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((i) => Math.min(flat.length - 1, i + 1));
      if (e.key === "ArrowLeft") setActive((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, flat.length]);

  return (
    <div className="min-h-screen bg-[#0f1b2d] text-white" data-testid="gallery-page" style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
      <style>{PRINT_CSS}</style>

      {/* ============ SCREEN VIEW ============ */}
      <div className="gallery-screen-only">
        {/* Title / cover */}
        <header className="border-b border-white/10 bg-[#426db0]">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div>
                <p className="text-white/70 text-sm font-semibold tracking-[0.2em] uppercase">BoxHero 3PL Platform</p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-2 leading-[1.05]">Product Screen Gallery</h1>
                <p className="text-white/85 text-base mt-4 max-w-2xl">
                  Every screen in the platform, captured live with real data. Click any screen to view it full-size
                  (← / → to move between screens), or export the whole set to a PDF deck.
                </p>
              </div>
              <button
                onClick={() => window.print()}
                data-testid="gallery-print-btn"
                className="shrink-0 inline-flex items-center gap-2 bg-white text-[#426db0] hover:bg-white/90 font-bold rounded-full px-6 py-3 shadow-lg transition-colors"
              >
                <Printer className="w-5 h-5" /> Save as PDF
              </button>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 text-sm">
              <span className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 font-semibold" data-testid="gallery-count">
                <Camera className="w-4 h-4" /> {shots.length} screens
              </span>
              {groups.map((g) => (
                <a
                  key={g.name}
                  href={`#${g.name.replace(/[^a-z]/gi, "")}`}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full px-4 py-1.5 font-semibold"
                  data-testid={`gallery-jump-${g.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: (GROUP_META[g.name] || {}).accent || "#fff" }} />
                  {g.name} · {g.items.length}
                </a>
              ))}
              <span className="inline-flex items-center bg-white/5 rounded-full px-4 py-1.5 text-white/60">{today}</span>
            </div>
          </div>
        </header>

        {/* Groups */}
        <main className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12 space-y-16">
          {shots.length === 0 && <p className="text-white/60" data-testid="gallery-empty">Loading screens…</p>}
          {groups.map((g) => {
            const meta = GROUP_META[g.name] || {};
            const key = g.name.replace(/[^a-z]/gi, "");
            return (
              <section key={g.name} id={key} data-testid={`gallery-group-${key.toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-1">
                  <span className="w-3.5 h-3.5 rounded-full" style={{ background: meta.accent || "#fff" }} />
                  <h2 className="text-2xl font-bold">{g.name}</h2>
                </div>
                <p className="text-white/60 text-sm mb-7 ml-6">{meta.blurb}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">
                  {g.items.map((s) => {
                    const idx = flat.findIndex((x) => x.slug === s.slug);
                    return (
                      <button
                        key={s.slug}
                        onClick={() => setActive(idx)}
                        className="group text-left bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/25 rounded-2xl overflow-hidden transition-all duration-200 hover:-translate-y-1"
                        data-testid={`gallery-card-${s.slug}`}
                      >
                        <div className="aspect-[16/10] overflow-hidden bg-[#0a1320] border-b border-white/10">
                          <img src={`/gallery/${s.file}`} alt={s.label} loading="lazy"
                            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-300" />
                        </div>
                        <div className="p-4">
                          <p className="font-bold text-[15px]">{s.label}</p>
                          <p className="text-white/45 text-xs font-mono mt-1">{s.route || s.landed}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
          <footer className="pt-8 border-t border-white/10 text-white/40 text-sm">
            Captured from the BoxHero preview build · {today}
          </footer>
        </main>
      </div>

      {/* ============ PRINT / PDF DECK (one screen per page) ============ */}
      <div className="gallery-print-only" style={{ display: "none", color: "#0f1b2d" }}>
        {/* Cover page */}
        <div className="print-slide" style={{ height: "192mm", display: "flex", flexDirection: "column", justifyContent: "center", background: "#426db0", color: "#fff", padding: "18mm", borderRadius: "6px" }}>
          <p style={{ letterSpacing: "0.2em", textTransform: "uppercase", fontSize: "12px", opacity: 0.8, fontWeight: 700 }}>BoxHero 3PL Platform</p>
          <h1 style={{ fontSize: "44px", fontWeight: 800, margin: "8px 0 12px" }}>Product Screen Gallery</h1>
          <p style={{ fontSize: "15px", opacity: 0.9, maxWidth: "180mm" }}>
            A complete walkthrough of every screen in the platform — the brand/customer portal and the internal warehouse operations console — captured live with real data.
          </p>
          <p style={{ marginTop: "18px", fontSize: "13px", opacity: 0.85 }}>{shots.length} screens · {today}</p>
        </div>
        {/* One slide per screen */}
        {flat.map((s, i) => (
          <div key={s.slug} className="print-slide" style={{ height: "192mm", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", borderBottom: "2px solid #e2e8f0", paddingBottom: "6px", marginBottom: "8px" }}>
              <div>
                <span style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.15em", color: (GROUP_META[s.group] || {}).accent || "#426db0", fontWeight: 700 }}>{s.group}</span>
                <h2 style={{ fontSize: "22px", fontWeight: 800, margin: "2px 0 0" }}>{s.label}</h2>
              </div>
              <div style={{ textAlign: "right", color: "#64748b", fontSize: "11px", fontFamily: "monospace" }}>
                <div>{s.route || s.landed}</div>
                <div>{i + 1} / {flat.length}</div>
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0 }}>
              <img src={`/gallery/${s.file}`} alt={s.label}
                style={{ maxWidth: "100%", maxHeight: "165mm", objectFit: "contain", border: "1px solid #e2e8f0", borderRadius: "4px" }} />
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {active !== null && flat[active] && (
        <div className="gallery-screen-only fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col" onClick={() => setActive(null)} data-testid="gallery-lightbox">
          <div className="flex items-center justify-between px-6 py-4 text-white" onClick={(e) => e.stopPropagation()}>
            <div>
              <p className="font-bold text-lg">{flat[active].label}</p>
              <p className="text-white/50 text-xs font-mono">{flat[active].group} · {flat[active].route} · {active + 1} / {flat.length}</p>
            </div>
            <button onClick={() => setActive(null)} className="p-2 rounded-full hover:bg-white/10" data-testid="gallery-close"><X className="w-6 h-6" /></button>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 pb-6 overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img src={`/gallery/${flat[active].file}`} alt={flat[active].label} className="max-w-full max-h-full rounded-lg shadow-2xl object-contain" />
          </div>
          <button onClick={(e) => { e.stopPropagation(); setActive((i) => Math.max(0, i - 1)); }} disabled={active === 0}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30" data-testid="gallery-prev">
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setActive((i) => Math.min(flat.length - 1, i + 1)); }} disabled={active === flat.length - 1}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30" data-testid="gallery-next">
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
      )}
    </div>
  );
}
